import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarPlus, Flag, Play } from "lucide-react";

import { transportService, type Allocation, type Route, type Trip, type Vehicle } from "../../services/transportService";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { IncidentForm } from "./IncidentsPanel";
import { CancelPanel, errorLine } from "./TransportParts";
import { BOARDING_LABELS, boardingChoices, boardingCounts, clock, day, isOpenTrip, tripConfig, tripRiders, tripStatus } from "./transportLogic";

function ScheduleTripForm({ routes, vehicles, onClose, onDone }: { routes: Route[]; vehicles: Vehicle[]; onClose: () => void; onDone: (trip: Trip) => void }) {
  const today = todayIso();
  const usable = vehicles.filter((vehicle) => vehicle.is_available && vehicle.assigned_driver);
  const [vehicleId, setVehicleId] = useState(usable[0] ? String(usable[0].id) : "");
  const vehicle = usable.find((row) => String(row.id) === vehicleId);
  const save = useMutation({ mutationFn: transportService.createTrip, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (!vehicle?.assigned_driver) return;
    save.mutate({ route_id: Number(data.get("route")), vehicle_id: vehicle.id, driver_id: vehicle.assigned_driver, trip_date: String(data.get("date")) });
  };
  return (
    <FormModal
      id="schedule-trip"
      title="Schedule a trip"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The trip could not be scheduled.") : undefined}
      saveLabel="Schedule the trip"
      note="Times come from the route. A bus and its driver take one trip a day."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The trip" divided={false}>
        <Field label="Route" className="sm:col-span-2">
          <select required name="route" className="input mt-1">
            <option value="">Choose a route</option>
            {routes.filter((route) => route.is_active && route.is_available).map((route) => (
              <option key={route.id} value={route.id}>
                {route.name} · leaves {clock(route.departure_time)} · {route.current_students} riders
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vehicle" hint={vehicle ? `Driven by ${vehicle.assigned_driver_name}; ${vehicle.seating_capacity} seats.` : "Only vehicles on the road with a driver are listed."}>
          <select required value={vehicleId} onChange={(event) => setVehicleId(event.target.value)} className="input mt-1">
            <option value="">Choose a vehicle</option>
            {usable.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.registration_number}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input required type="date" name="date" min={today} max={daysFromTodayIso(365)} defaultValue={today} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function CompleteTripForm({ trip, onClose, onDone }: { trip: Trip; onClose: () => void; onDone: (trip: Trip) => void }) {
  const save = useMutation({ mutationFn: transportService.completeTrip, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      id: trip.id,
      distance_covered: text("distance"),
      fuel_consumed: text("fuel"),
      traffic_conditions: text("traffic"),
      weather_conditions: text("weather"),
      issues_encountered: text("issues"),
      notes: text("notes"),
    });
  };
  return (
    <FormModal
      id="complete-trip"
      title={`Trip done · ${trip.route_name}`}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The trip could not be completed.") : undefined}
      saveLabel="Mark the trip done"
      note="Arrival is recorded as now."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The journey" description="All optional; fill in what the driver knows." divided={false}>
        <Field label="Distance (km)" optional>
          <input type="number" name="distance" min={0} step="0.1" className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Fuel used (litres)" optional>
          <input type="number" name="fuel" min={0} step="0.1" className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Traffic" optional>
          <input name="traffic" list="traffic-levels" maxLength={50} className="input mt-1" />
          <datalist id="traffic-levels">{["Light", "Normal", "Heavy", "Jam"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
        <Field label="Weather" optional>
          <input name="weather" list="weather-kinds" maxLength={50} className="input mt-1" />
          <datalist id="weather-kinds">{["Dry", "Rain", "Heavy rain", "Fog"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
        <Field label="Problems on the way" optional className="sm:col-span-2" hint="Anything serious is better reported as an incident.">
          <textarea name="issues" rows={2} className="input mt-1" />
        </Field>
        <Field label="Notes" optional className="sm:col-span-2">
          <textarea name="notes" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Who is on the bus: every rider on the route, in stop order, marked as the bus goes. */
function BoardingSheet({ trip, riders, canMark }: { trip: Trip; riders: Allocation[]; canMark: boolean }) {
  const queryClient = useQueryClient();
  const marks = useQuery({ queryKey: ["transport-boarding", trip.id], queryFn: () => transportService.getTripAttendance(trip.id) });
  const mark = useMutation({
    mutationFn: transportService.markAttendance,
    onSuccess: () => Promise.all([queryClient.invalidateQueries({ queryKey: ["transport-boarding", trip.id] }), queryClient.invalidateQueries({ queryKey: ["transport-trips"] })]),
  });
  const byLearner = new Map((marks.data ?? []).map((row) => [row.student, row]));
  const counts = boardingCounts(riders, marks.data ?? []);
  const live = trip.status === "in_progress";

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Boarding sheet ({riders.length} {riders.length === 1 ? "rider" : "riders"})</h4>
        <p className="text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{counts.onBus}</span> on the bus · {counts.droppedOff} dropped off · {counts.absent} absent · {counts.unmarked} not marked
        </p>
      </div>
      {marks.isLoading ? (
        <div className="mt-1.5 h-20 animate-pulse rounded bg-gray-100" />
      ) : !riders.length ? (
        <p className="mt-1.5 text-sm text-gray-500">No learners ride this route yet.</p>
      ) : (
        <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
          {riders.map((rider) => {
            const current = byLearner.get(rider.student);
            return (
              <li key={rider.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="font-medium text-gray-900">{rider.student_name}</span>
                  <span className="block text-xs text-gray-500">
                    {rider.pickup_stop_name} → {rider.dropoff_stop_name}
                    {rider.medical_conditions ? " · health note" : ""}
                  </span>
                </span>
                {canMark && live ? (
                  <span role="group" aria-label={`Mark ${rider.student_name}`} className="segmented">
                    {boardingChoices(current?.status).map((choice) => (
                      <button
                        key={choice.value}
                        type="button"
                        aria-pressed={current?.status === choice.value}
                        disabled={choice.disabled && current?.status !== choice.value ? true : mark.isPending}
                        onClick={() => current?.status !== choice.value && mark.mutate({ trip_id: trip.id, student_id: rider.student, status: choice.value })}
                        className="segmented-option disabled:cursor-not-allowed disabled:opacity-40 aria-pressed:opacity-100"
                      >
                        {choice.label}
                      </button>
                    ))}
                  </span>
                ) : (
                  <span className="text-xs font-medium text-gray-700">
                    {current ? `${BOARDING_LABELS[current.status] ?? current.status}${current.pickup_time ? ` · on ${clock(current.pickup_time)}` : ""}${current.dropoff_time ? ` · off ${clock(current.dropoff_time)}` : ""}` : "Not marked"}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {mark.isError && <div className="mt-2">{errorLine(mark.error, "The mark could not be saved.")}</div>}
      {riders.some((rider) => rider.medical_conditions) && (
        <div className="mt-2 space-y-1 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {riders.filter((rider) => rider.medical_conditions).map((rider) => (
            <p key={rider.id}><span className="font-semibold">{rider.student_name}:</span> {rider.medical_conditions}</p>
          ))}
        </div>
      )}
    </section>
  );
}

/** Today's runs, what is coming up and what is done. Drivers start, mark and finish their own trips. */
export function TripsPanel({ trips, routes, vehicles, allocations, isLoading, isError, onRetry, canManage, canOperate, onChanged , initialView}: {
  /** A register view to open on, e.g. "papers" from the dashboard. */
  initialView?: string;
  trips?: Trip[];
  routes: Route[];
  vehicles: Vehicle[];
  allocations: Allocation[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  canOperate: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reporting, setReporting] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const start = useMutation({ mutationFn: transportService.startTrip, onSuccess: (trip) => done(`${trip.route_name} has left. Mark riders as they board.`) });
  const cancel = useMutation({ mutationFn: transportService.cancelTrip, onSuccess: (trip) => done(`${trip.route_name} on ${day(trip.trip_date)} was called off.`, true) });
  const stops = routes.flatMap((route) => route.stops);

  const columns: RegisterColumn<Trip>[] = [
    { header: "Date", cell: (row) => (row.trip_date === today ? "Today" : day(row.trip_date)), exportValue: (row) => day(row.trip_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Leaves", cell: (row) => clock(row.scheduled_departure), exportValue: (row) => clock(row.scheduled_departure), className: "tabular-nums text-gray-700" },
    { header: "Route", cell: (row) => <span className="font-medium text-gray-900">{row.route_name}</span>, exportValue: (row) => row.route_name, className: "min-w-[10rem]" },
    { header: "Vehicle", cell: (row) => `${row.vehicle_name}${row.vehicle_registration ? ` · ${row.vehicle_registration}` : ""}`, exportValue: (row) => `${row.vehicle_name} ${row.vehicle_registration ?? ""}`.trim(), className: "text-gray-700" },
    { header: "Driver", cell: (row) => row.driver_name, exportValue: (row) => row.driver_name, className: "text-gray-700" },
    { header: "On board", cell: (row) => `${row.students_onboard} / ${row.max_capacity}`, exportValue: (row) => row.students_onboard, align: "right" },
    {
      header: "Timing",
      cell: (row) =>
        row.actual_departure ? (
          <span className={row.is_delayed ? "text-amber-800" : "text-gray-700"}>
            Left {clock(row.actual_departure)}{row.is_delayed ? ` (${row.delay_minutes} min late)` : ""}
            {row.actual_arrival ? ` · back ${clock(row.actual_arrival)}` : ""}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
      exportValue: (row) => [row.actual_departure && `Left ${clock(row.actual_departure)}`, row.actual_arrival && `back ${clock(row.actual_arrival)}`].filter(Boolean).join(", "),
      className: "whitespace-nowrap",
    },
    { header: "Status", cell: (row) => <StatusDot {...tripStatus(row, today)} />, exportValue: (row) => tripStatus(row, today).label },
  ];

  const trip = viewing !== null ? trips?.find((row) => row.id === viewing) : undefined;
  const riders = trip ? tripRiders(trip, allocations, stops) : [];
  const route = trip ? routes.find((row) => row.id === trip.route) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={`${trips ? "loaded" : "loading"}-${initialView ?? ""}`}
        title="Trips"
        description={canOperate ? "Open a trip to start it, mark who boards, and finish it." : "When the bus runs, and whether it has left."}
        actions={canManage && (
          <button type="button" onClick={() => setScheduling(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Schedule a trip
          </button>
        )}
        rows={trips}
        config={tripConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          start.reset();
          cancel.reset();
          setViewing(row.id);
        }}
        openLabel={(row) => (!canOperate ? "Open" : row.status === "in_progress" ? "Boarding" : row.status === "scheduled" && row.trip_date === today ? "Start" : "Open")}
        noun={["trip", "trips"]}
        exportName={{ file: "transport-trips", title: "Transport Trips" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No trips yet. Schedule the first with Schedule a trip." : "No trips have been scheduled."}
        initialState={initialView ? { view: initialView } : trips?.some((row) => row.trip_date === today) ? undefined : { view: "all" }}
      />

      {trip && !completing && !reporting && (
        <DetailsModal
          size="3xl"
          title={`${trip.route_name} · ${trip.trip_date === today ? "today" : day(trip.trip_date)}`}
          headline={{
            label: trip.status === "in_progress" ? "On the bus now" : "Leaves",
            value: trip.status === "in_progress" ? `${trip.students_onboard} of ${trip.max_capacity}` : clock(trip.scheduled_departure),
            note: `${trip.vehicle_name}${trip.vehicle_registration ? ` (${trip.vehicle_registration})` : ""} · ${trip.driver_name}`,
          }}
          status={tripStatus(trip, today)}
          facts={[
            ["Route", route ? `${route.start_location} → ${route.end_location}` : trip.route_name],
            ["Due", `${clock(trip.scheduled_departure)} → ${clock(trip.scheduled_arrival)}`],
            ["Left", trip.actual_departure ? `${clock(trip.actual_departure)}${trip.is_delayed ? ` (${trip.delay_minutes} min late)` : ""}` : undefined],
            ["Back", clock(trip.actual_arrival) || undefined],
            ["Distance", trip.distance_covered ? `${Number(trip.distance_covered)} km` : undefined],
            ["Fuel", trip.fuel_consumed ? `${Number(trip.fuel_consumed)} litres` : undefined],
            ["Traffic", trip.traffic_conditions ?? undefined],
            ["Weather", trip.weather_conditions ?? undefined],
          ]}
          sections={[
            { title: "Problems on the way", body: trip.issues_encountered, tone: "warning" },
            { title: "Notes", body: trip.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canOperate && (trip.status === "in_progress" || trip.status === "completed") && trip.trip_date <= today && (
                <button type="button" className="btn btn-outline gap-1.5" onClick={() => setReporting(true)}>
                  <AlertTriangle className="h-4 w-4" /> Report an incident
                </button>
              )}
              {canOperate && trip.status === "scheduled" && trip.trip_date === today && (
                <button type="button" disabled={start.isPending} onClick={() => start.mutate(trip.id)} className="btn btn-primary gap-1.5">
                  <Play className="h-4 w-4" /> {start.isPending ? "Starting…" : "The bus has left"}
                </button>
              )}
              {canOperate && trip.status === "in_progress" && (
                <button type="button" disabled={trip.students_onboard > 0} onClick={() => setCompleting(true)} className="btn btn-primary gap-1.5">
                  <Flag className="h-4 w-4" /> Trip done
                </button>
              )}
            </>
          }
        >
          {canOperate && trip.status === "in_progress" && trip.students_onboard > 0 && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {trip.students_onboard} still on the bus. Mark them dropped off (or absent, if marked by mistake) before finishing the trip.
            </p>
          )}
          {canOperate && trip.status === "scheduled" && trip.trip_date > today && (
            <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">It can be started on {day(trip.trip_date)}.</p>
          )}
          {start.isError && errorLine(start.error, "The trip could not be started.")}

          {canOperate && trip.status !== "cancelled" && <BoardingSheet trip={trip} riders={riders} canMark={canOperate} />}

          {canManage && trip.status === "scheduled" && (
            <CancelPanel
              title="Call off the trip"
              description="Frees the bus and driver for the day. Families are not told automatically."
              placeholder="e.g. The road to the estate is flooded."
              context={{ key: "transport.note", params: { kind: "cancel", subject: "trip", id: trip.id } }}
              button="Call off the trip"
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The trip could not be called off."
              onCancel={(reason) => cancel.mutate({ id: trip.id, reason })}
            />
          )}
          {!canOperate && isOpenTrip(trip) && (
            <ActionPanel title="Where is the bus?">
              <p className="text-sm text-gray-700">
                {trip.status === "in_progress" ? `It left at ${clock(trip.actual_departure)} and is due at ${clock(trip.scheduled_arrival)}.` : `It is due to leave at ${clock(trip.scheduled_departure)}.`}
              </p>
            </ActionPanel>
          )}
        </DetailsModal>
      )}

      {trip && completing && (
        <CompleteTripForm
          trip={trip}
          onClose={() => setCompleting(false)}
          onDone={(saved) => {
            setCompleting(false);
            done(`${saved.route_name} is done; back at ${clock(saved.actual_arrival)}.`, true);
          }}
        />
      )}
      {trip && reporting && (
        <IncidentForm
          trips={[trip]}
          allocations={allocations}
          fixedTrip={trip}
          onClose={() => setReporting(false)}
          onDone={(incident) => {
            setReporting(false);
            done(`Incident “${incident.title}” reported. Follow it up under Incidents.`);
          }}
        />
      )}
      {scheduling && (
        <ScheduleTripForm
          routes={routes}
          vehicles={vehicles}
          onClose={() => setScheduling(false)}
          onDone={(saved) => {
            setScheduling(false);
            done(`${saved.route_name} scheduled for ${saved.trip_date === today ? "today" : day(saved.trip_date)}, leaving ${clock(saved.scheduled_departure)}.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
